import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Avatar, AvatarGroup } from "components";
import { useGlobalStore, useSendChatMessage } from "controller";
import { useGetThreadMessages } from "controller/hooks/chat/useThreadMessages";
import dayjs from "dayjs";
import { useAtomValue, useSetAtom } from "jotai";
import {
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import {
  AttachmentMessageBlock,
  MemberTimelineBlock,
  MessageFooter,
} from "shared/features/chat/MessageBlocks";
import { SafeHtmlRenderer } from "shared/features/chat/SafeHtmlRenderer";
import {
  chatThreadParentMessage,
  chatThreadSendMode,
  clearChatReplyToMessage,
  setChatThreadSendMode,
} from "shared/features/chat/store";
import type { ChatMessageInputRef } from "shared/features/chat/TipTapChatInput";
import { stripHtml } from "shared/features/chat/utils/stripHtml";
import type { AccessSpecifier, ThreadMessage } from "shared/types";
import { PinnedChatInputSection } from "./PinnedChatInputSection";

// ─── Helper ──────────────────────────────────────────────────────────

function getThreadMessageType(message: ThreadMessage): "resource" | "customer" {
  if (message.sender.user_type !== "resource") return "customer";
  return "resource";
}

// ─── Thread Summary Bar ──────────────────────────────────────────────

interface ThreadSummaryBarProps {
  members: Array<{
    display_name: string;
    profile_pic?: string;
    first_name?: string;
    last_name?: string;
  }>;
  replyCount: number;
}

const ThreadSummaryBar = memo(function ThreadSummaryBar({
  members,
  replyCount,
}: ThreadSummaryBarProps) {
  const theme = useTheme();

  const getSummaryText = (): string => {
    if (members.length === 0)
      return `${replyCount} ${replyCount === 1 ? "Reply" : "Replies"}`;
    const names = members.map((m) => m.display_name || "Unknown");
    if (names.length <= 2) {
      return `Reply from ${names.join(", ")}`;
    }
    return `Reply from ${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  const avatarDataList = members.slice(0, 4).map((m) => ({
    profile_pic: m.profile_pic ?? "",
    first_name: m.first_name ?? m.display_name ?? "",
    last_name: m.last_name ?? "",
  }));

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      sx={{
        mx: "12px",
        my: "8px",
        p: "8px 12px 8px 20px",
        borderRadius: "8px",
        backgroundColor: theme.palette.neutral.main,
      }}
    >
      {avatarDataList.length > 0 && (
        <AvatarGroup
          max={4}
          childWidthHeight="24px"
          overlapMargin="-8px"
          showExtrasNumberingAvatar={false}
        >
          {avatarDataList.map((member) => (
            <Avatar
              key={`${member.first_name}-${member.last_name}`}
              url={member.profile_pic}
              fullname={`${member.first_name} ${member.last_name}`.trim()}
              variants="sm"
            />
          ))}
        </AvatarGroup>
      )}
      <Typography
        variant="caption-2"
        sx={{
          color: theme.palette.lightBg.high,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {getSummaryText()}
      </Typography>
    </Stack>
  );
});

// ─── Thread Reply Row ────────────────────────────────────────────────

interface ThreadReplyRowProps {
  message: ThreadMessage;
  isFirstInGroup?: boolean;
}

const ThreadReplyRow = memo(function ThreadReplyRow({
  message,
  isFirstInGroup = true,
}: ThreadReplyRowProps) {
  const theme = useTheme();
  const type = getThreadMessageType(message);
  const timezone = useGlobalStore((state) => state.timezone);

  const hasAttachments = message.attachments && message.attachments.length > 0;
  return (
    <Box
      sx={{
        ml: "12px",
        pl: "12px",
        pt: isFirstInGroup ? "16px" : "2px",
        pr: "12px",
      }}
    >
      {/* Header: Avatar + Name + dot + Time (only for first in group) */}
      {isFirstInGroup && (
        <Stack direction="row" alignItems="center" gap="8px">
          <Avatar
            variants="sm"
            fullname={message.sender.display_name}
            url={message.sender.profile_pic ?? ""}
          />
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.lightBg.high,
              textTransform: "capitalize",
            }}
          >
            {message.sender.display_name}
          </Typography>
          <Box
            sx={{
              width: "4px",
              height: "4px",
              borderRadius: "50%",
              backgroundColor: theme.palette.lightBg.low,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low }}
          >
            {dayjs(message.created_at)
              .tz(timezone)
              .format("h:mmA")
              .toLowerCase()}
          </Typography>
        </Stack>
      )}

      {/* Attachments */}
      {hasAttachments &&
        message.attachments.map((att) => (
          <Box
            key={att._id}
            sx={{
              mt: "8px",
              ml: "32px",
              maxWidth: "100%",
              overflowWrap: "break-word",
            }}
          >
            <AttachmentMessageBlock attachment={att} />
          </Box>
        ))}

      {/* Message body */}
      <Box
        sx={{
          backgroundColor: theme.palette.neutral["010"],
          borderRadius: "0 12px 12px 12px",
          p: "8px 12px",
          width: "fit-content",
          maxWidth: "calc(100% - 32px)",
          border: `1px solid ${theme.palette.neutral["015"]}`,
          ml: "32px",
        }}
      >
        <Box
          sx={{
            ...theme.typography["body-medium"],
            color: theme.palette.lightBg.high,
            wordBreak: "break-word",
          }}
        >
          <SafeHtmlRenderer html={message.message} />
        </Box>

        {/* Footer: access specifier, edited, platform */}
        <Box>
          <MessageFooter
            senderType={type}
            accessSpecifier={message.access_specifier}
            isEdited={message.editing?.is_edited}
            messagePlatform={message.message_platform ?? null}
            sentAsEmail={message.send_as_email}
            type={
              message.message_platform === "email"
                ? "email"
                : ((message.message_type ?? "normal") as
                    | "canned_notes"
                    | "time_entry_notes"
                    | "normal")
            }
          />
        </Box>
      </Box>
    </Box>
  );
});

// ─── Virtuoso List Items ─────────────────────────────────────────────

type PinnedThreadListItem =
  | { kind: "parent-text"; html: string; key: string }
  | {
      kind: "summary";
      members: ThreadSummaryBarProps["members"];
      replyCount: number;
      key: string;
    }
  | {
      kind: "reply";
      message: ThreadMessage;
      key: string;
      isFirstInGroup: boolean;
      isLastInGroup: boolean;
    };

/**
 * Mark adjacent replies from the same sender within 15 min as grouped.
 * Timeline messages are excluded — they don't use grouping flags.
 */
function applySenderGrouping(items: PinnedThreadListItem[]): void {
  const GROUP_TIME_THRESHOLD_MS = 15 * 60000;

  for (let i = 1; i < items.length; i++) {
    const curr = items[i];
    const prev = items[i - 1];
    if (curr.kind !== "reply" || prev.kind !== "reply") continue;
    if (
      curr.message.message_type === "member_timeline" ||
      prev.message.message_type === "member_timeline"
    )
      continue;

    const sameSender =
      curr.message.sender.mapping_id === prev.message.sender.mapping_id;
    const timeDiff = Math.abs(
      new Date(curr.message.created_at).getTime() -
        new Date(prev.message.created_at).getTime(),
    );

    if (sameSender && timeDiff <= GROUP_TIME_THRESHOLD_MS) {
      curr.isFirstInGroup = false;
      prev.isLastInGroup = false;
    }
  }
}

function buildPinnedThreadFlatList(
  parentMessage: ThreadMessage | undefined,
  replies: ThreadMessage[],
  replyCount: number,
  threadMembers: ThreadSummaryBarProps["members"],
): PinnedThreadListItem[] {
  const items: PinnedThreadListItem[] = [];

  if (parentMessage) {
    items.push({
      kind: "parent-text",
      html: parentMessage.message,
      key: `parent-${parentMessage.chat_id}`,
    });
  }

  items.push({
    kind: "summary",
    members: threadMembers,
    replyCount,
    key: "thread-summary",
  });

  for (const reply of replies) {
    items.push({
      kind: "reply",
      message: reply,
      key: reply.chat_id,
      isFirstInGroup: true,
      isLastInGroup: true,
    });
  }

  applySenderGrouping(items);

  return items;
}

// ─── Thread Timeline Row ─────────────────────────────────────────────

const ThreadTimelineRow = memo(function ThreadTimelineRow({
  message,
  resourceId,
}: {
  message: ThreadMessage;
  resourceId: string;
}) {
  return (
    <MemberTimelineBlock
      sender={message.sender}
      timelineMeta={message.timeline_meta}
      resourceId={resourceId}
    />
  );
});

// ─── Parent Message Plain Text ───────────────────────────────────────

const ParentMessageText = memo(function ParentMessageText({
  html,
}: {
  html: string;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        px: "16px",
        py: "12px",
        ...theme.typography["body-medium"],
        color: theme.palette.lightBg.high,
        wordBreak: "break-word",
      }}
    >
      <SafeHtmlRenderer html={html} />
    </Box>
  );
});

// ─── PinnedThreadView ────────────────────────────────────────────────

interface PinnedThreadViewProps {
  serviceType: "ticket" | "task";
  serviceId: string;
  isDisabled: boolean;
}

export const PinnedThreadView = memo(function PinnedThreadView({
  serviceType,
  serviceId,
  isDisabled,
}: PinnedThreadViewProps) {
  const theme = useTheme();
  const resourceId = useGlobalStore((state) => state.resourceId);
  const parentMessage = useAtomValue(chatThreadParentMessage);
  const clearReplyAtom = useSetAtom(clearChatReplyToMessage);
  const editorRef = useRef<ChatMessageInputRef>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null) as RefObject<VirtuosoHandle>;

  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const threadId = parentMessage?.thread_id ?? null;

  const { data, isLoading } = useGetThreadMessages(threadId);

  const { mutateAsync: sendMessage } = useSendChatMessage();

  // Build the thread access specifier from the thread data or parent message
  const threadAccessSpecifier: AccessSpecifier =
    data?.data?.thread?.access_specifier ??
    parentMessage?.access_specifier ??
    "private";

  // ─── Thread send mode (synced from thread access specifier) ──────
  const threadSendMode = useAtomValue(chatThreadSendMode);
  const setThreadSendMode = useSetAtom(setChatThreadSendMode);

  useEffect(() => {
    setThreadSendMode(threadAccessSpecifier);
  }, [threadAccessSpecifier, setThreadSendMode]);

  // Build unique reply participants for the summary bar
  const replyParticipants = useMemo(() => {
    if (!data?.data?.messages) return [];
    const seen = new Set<string>();
    const participants: ThreadSummaryBarProps["members"] = [];
    for (const msg of data.data.messages) {
      const id = msg.sender.mapping_id;
      if (!seen.has(id)) {
        seen.add(id);
        participants.push({
          display_name: msg.sender.display_name,
          profile_pic: msg.sender.profile_pic,
        });
      }
    }
    return participants;
  }, [data?.data?.messages]);

  // Build flat list for Virtuoso
  const threadFlatList = useMemo<PinnedThreadListItem[]>(() => {
    if (!data?.data) return [];
    return buildPinnedThreadFlatList(
      data.data.parent_message,
      data.data.messages,
      data.pagination?.total ?? data.data.messages.length,
      replyParticipants,
    );
  }, [data, replyParticipants]);

  // Scroll to last message when thread data loads (top-aligned when few items)
  useEffect(() => {
    if (threadFlatList.length > 0) {
      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: threadFlatList.length - 1,
          align: "end",
          behavior: "auto",
        });
      });
    }
  }, [threadFlatList.length]);

  // ─── Thread reply fields (derived from parent) ────────────────────

  const parentMessageId = parentMessage?.chat_id ?? null;
  const parentExcerpt = parentMessage?.message
    ? stripHtml(parentMessage.message).slice(0, 500)
    : null;

  // ─── Send handler ──────────────────────────────────────────────────

  const handleThreadSend = useCallback(
    async ({
      messageText,
      attachmentArray,
      mentionedUsers,
      mentionedEmails,
    }: {
      messageText: string;
      attachmentArray?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
    }) => {
      if (isSendingMessage || isDisabled) return;

      const trimmedValue = messageText.trim();
      const hasContent =
        trimmedValue.length > 0 || Boolean(attachmentArray?.length);
      if (!hasContent) return;

      setIsSendingMessage(true);
      try {
        await sendMessage({
          message: trimmedValue,
          accessSpecifier: threadSendMode,
          serviceId,
          serviceType,
          messageType: "normal",
          ...(threadId && { thread_id: threadId }),
          ...(parentMessageId && { parent_message_id: parentMessageId }),
          ...(parentExcerpt && { excerpt: parentExcerpt }),
          ...(attachmentArray?.length && { attachments: attachmentArray }),
          ...(mentionedUsers?.length && { mentioned_users: mentionedUsers }),
          ...(mentionedEmails?.length && { mentioned_emails: mentionedEmails }),
        });

        clearReplyAtom();
        editorRef.current?.clear();

        // Thread cache is updated by MQTT via PinnedChatPanel.handleMqttThreadReply
        // -> addThreadReplyToCache. No invalidateQueries needed (per 13-CORRECTIONS.md).
      } catch {
        // Error handled by mutation hook / apiErrorHandler
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      isSendingMessage,
      isDisabled,
      sendMessage,
      threadSendMode,
      serviceId,
      serviceType,
      threadId,
      parentMessageId,
      parentExcerpt,
      clearReplyAtom,
    ],
  );

  // ─── Virtuoso render item ──────────────────────────────────────────

  const renderItem = useCallback(
    (_index: number, item: PinnedThreadListItem) => {
      if (item.kind === "parent-text") {
        return <ParentMessageText html={item.html} />;
      }

      if (item.kind === "summary") {
        return (
          <ThreadSummaryBar
            members={item.members}
            replyCount={item.replyCount}
          />
        );
      }

      // kind === "reply"
      if (item.message.message_type === "member_timeline") {
        return (
          <ThreadTimelineRow
            message={item.message}
            resourceId={resourceId ?? ""}
          />
        );
      }

      return (
        <ThreadReplyRow
          message={item.message}
          isFirstInGroup={item.isFirstInGroup}
        />
      );
    },
    [resourceId],
  );

  const Footer = useCallback(() => <Box sx={{ pb: "48px" }} />, []);

  // ─── Empty state ───────────────────────────────────────────────────

  if (!parentMessage) {
    return (
      <Stack
        sx={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Select a thread to view
        </Typography>
      </Stack>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <Stack
      sx={{ flex: 1, overflow: "hidden" }}
      direction="column"
      justifyContent="space-between"
    >
      {isLoading ? (
        <Stack
          sx={{
            flexGrow: 1,
            justifyContent: "center",
            alignItems: "center",
            padding: "48px",
          }}
        >
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Virtuoso
          ref={virtuosoRef}
          data={threadFlatList}
          followOutput="smooth"
          skipAnimationFrameInResizeObserver
          increaseViewportBy={{ top: 400, bottom: 200 }}
          itemContent={renderItem}
          components={{ Footer }}
          style={{
            flexGrow: 1,
            width: "100%",
          }}
        />
      )}
      <PinnedChatInputSection
        serviceType={serviceType}
        serviceId={serviceId}
        isDisabled={isDisabled}
        isSendingMessage={isSendingMessage}
        editorRef={editorRef}
        sendMode={threadSendMode}
        onModeChange={setThreadSendMode}
        onSend={handleThreadSend}
      />
    </Stack>
  );
});
